import {DataResponse, RequestStatus} from "../tlDataServices/DataResponse";
import {DataRequest} from "../tlDataServices/DataRequest";
import * as angular from 'angular';

export interface IConflictResult<T> {
  reject?: boolean;
  resolveObject?: T;
  accept?: boolean;
}
export interface IConflict<T> {
  local: IStorageItem<T>;
  remote: IStorageItem<T>;
}
export interface IGetOptions<T> {
  changed? (update: T): void;
  conflict? (conflic: IConflict<T>): IConflictResult<T>;
}

export interface IRequestOptions<T> {
  success(T): void;
  timeout(T): void;
  fail(T): void;
}
export interface IDataStorage {
  get<T>(key: string): IStorageItem<T>;
  set<T>(key: string, storageItem: IStorageItem<T>): void;
  remove(key: string);
}
export class LocalDataStorage implements IDataStorage {
  remove(key: string) {
    localStorage.removeItem(key);
  }

  get(key: string) {
    var item = localStorage.getItem(key);
    if (item !== null) {
      try {
        return angular.fromJson(item);
      } catch (e) {
        // todo log
      }
    }
    return null;
  }
  set<T>(key: string, storageItem: IStorageItem<T>) {
    if (!angular.isDate(storageItem.creationDate)) {
      storageItem.creationDate = new Date();
    }

    var item = localStorage.getItem(key);
    if (item == null) {
      this.addNew(key, storageItem);
    } else {
      // todo implement update case, exception handling
      try {
        var fromJson: IStorageItem<T> = angular.fromJson(item);
        if (angular.isObject(fromJson)) {
          fromJson.localUpdateDate = new Date();
          fromJson.data = storageItem.data;
          fromJson.eTag = storageItem.eTag;
          this.setItem(key, fromJson);
        } else {
          // todo log
          this.addNew(key, storageItem);
        }
      } catch (e) {
        // todo log
        this.addNew(key, storageItem);
      }
    }
  }
  private addNew<T>(key: string, storageItem: IStorageItem<T>) {
    storageItem.localUpdateDate = new Date();
    this.setItem(key, storageItem);
  }
  private setItem<T>(key: string, storageItem: IStorageItem<T>) {
    var json = angular.toJson(storageItem, false);
    localStorage.setItem(key, json);
  }
}
export interface IStorageItem<T> {
  creationDate: Date;
  localUpdateDate: Date;
  eTag: string;
  data: T;
}
export class WebSqlDataStorage implements IDataStorage {
  remove(key: string) {

  }
  get(url: string) {
    return null;
  }

  set<T>(key: string, storageItem: IStorageItem<T>) {

  }
}
export enum DataStorageType {
  LocalStorage,
  WebSql
}
export interface IDataTransaction {
  dataRequest: DataRequest<any>;
  storageItem: IStorageItem<any>;
  started: Date;
  ended: Date;
  result: DataResponse<any>;
  source: string;
}
export class RemoteDataStore {
  public static serviceId = "$remoteData";
  public static $inject = ["$http", "$q", "$requestSender"];
  private _dataStorage: IDataStorage;
  private _transactions: IDataTransaction[] = [];
  constructor(private $http: angular.IHttpService, private $q: angular.IQService, private $requestSender) {
    this._dataStorage = new LocalDataStorage();
  }
  public get<T>(dataRequest: DataRequest<T>, getOptions?: IGetOptions<T>): angular.IPromise<DataResponse<T>> {
    var defer = this.$q.defer<DataResponse<T>>();
    var loadedHandler = angular.noop;
    var changedHandler = angular.noop;
    if (angular.isObject(getOptions)) {
      if (angular.isFunction(getOptions.changed))
        changedHandler = getOptions.changed;
    }
    var startTime = new Date();
    var storageItem = this.getLocal(dataRequest);
    if (storageItem == null) {
      var reqPromise = this.$requestSender.requestValue(dataRequest);
      return this.updateStore<T>(dataRequest, reqPromise).then(r => {
        this._transactions.push({
          dataRequest: dataRequest,
          ended: new Date(),
          result: r,
          source: "initial server",
          started: startTime,
          storageItem: this.getLocal(dataRequest)
        });
        return r;
      }, e=> e);
    } else {
      var response = new DataResponse(storageItem.data, RequestStatus.Success);
      defer.resolve(response);
      // todo start triggering update
      var reqPromise = this.$requestSender.requestValue(dataRequest);
      this._transactions.push({
        dataRequest: dataRequest,
        ended: new Date(),
        result: response,
        source: "local",
        started: startTime,
        storageItem: storageItem
      });
      this.updateStore<T>(dataRequest, reqPromise).then(p => {
        this._transactions.push({
          dataRequest: dataRequest,
          ended: new Date(),
          result: p,
          source: "update server",
          started: startTime,
          storageItem: this.getLocal(dataRequest)
        });
        changedHandler(p.data);
      }, (err) => {
        // todo
      });
    }
    return defer.promise;
  }
  public getLocal<T>(dataRequest: DataRequest<T>): IStorageItem<T> {
    return this._dataStorage.get<T>(this.getKey(dataRequest));
  }
  public removeLocal<T>(dataRequest: DataRequest<T>) {
    return this._dataStorage.remove(this.getKey(dataRequest));
  }
  public getTransactions<T>(): IDataTransaction[] {
    return this._transactions;
  }
  private getKey<T>(dataRequest: DataRequest<T>) {
    // todo add post support
    return dataRequest.url;
  }
  private setDataStorage(dataStorage: DataStorageType) {
    if (dataStorage == DataStorageType.LocalStorage) {
      this._dataStorage = new LocalDataStorage();
    } else {
      this._dataStorage = new WebSqlDataStorage();
    }
  }
  public enqueueRequest<TResult, TRequest extends DataRequest<any>>(request: (any) => angular.IPromise<TResult>, requestOptions: IRequestOptions<TRequest>) {

  }

  private updateStore<T>(dataRequest: DataRequest<T>, reqPromise: angular.IPromise<DataResponse<T>>): angular.IPromise<DataResponse<T>> {
    return reqPromise.then(req => {
      if (req.isSuccessful()) {
        this.storeData(RemoteDataStore.getKey(dataRequest), req);
      } else {
        // todo reschedule
      }
      return req;
    }, err => {
      return err;
      // todo define
    });
  }
  private static getKey<T>(dataRequest: DataRequest<T>) {
    // todo check verb
    if (dataRequest.method.toUpperCase() == "GET") {
      return dataRequest.url;
    } else {
      throw "not supported http verb for remote storage";
    }
  }
  private storeData<T>(key: string, response: DataResponse<T>) {
    var storageItem: IStorageItem<T> = {
      creationDate: new Date(),
      data: response.data,
      eTag: response.httpReponse.headers["etag"],
      localUpdateDate: new Date()
    };
    this._dataStorage.set(key, storageItem);
  }
}


